import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { SaleOrderFbaLabel, useFbaLabelUpload } from 'src/services/saleOrder';

const MAX_FBA_LABEL_SIZE = 10 * 1024 * 1024; // 10 MiB, mirrors the backend limit

export interface FbaLabelProps {
  saleOrderId: number;
  current: SaleOrderFbaLabel;
}

const isPdfFile = (file: File) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

// Maps the enriched error thrown by useFbaLabelUpload's mutationFn (status +
// detail, see saleOrder.ts) to the inline/toast text. FORBIDDEN_STATE is
// checked first since a cancelled order is a 403 that would otherwise fall
// through to the generic branch. `detail` is a full backend sentence ending
// in "... Reason: FORBIDDEN_STATE", not the bare code, so this matches via
// `includes` — same pattern as the create/update error mapping in
// useOutboundOrder.ts / CreateSaleOrderOverlay.tsx (INCOTERM_NOT_ENABLED /
// COD_NOT_ENABLED).
const mapUploadError = (err: unknown, t: (key: string) => string): string => {
  const typed = err as { status?: number; detail?: unknown } | null | undefined;
  if (typeof typed?.detail === 'string' && typed.detail.includes('FORBIDDEN_STATE')) {
    return t('errors.fba_label_order_cancelled');
  }
  if (typeof typed?.detail === 'string' && typed.detail.includes('FBA_LABEL_NOT_ENABLED')) {
    return t('errors.fba_label_not_enabled');
  }
  if (typed?.status === 413) return t('errors.fba_label_too_large');
  if (typed?.status === 415) return t('errors.fba_label_not_pdf');
  return t('errors.fba_label_upload_failed');
};

const FbaLabel: React.FC<FbaLabelProps> = ({ saleOrderId, current }) => {
  const { t } = useTranslation();
  const { upload, isPending } = useFbaLabelUpload();
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingFileRef = useRef<File | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runUpload = async (file: File) => {
    try {
      await upload(saleOrderId, file);
      setFieldError(null);
      toast.success(t('success.fba_label_uploaded'));
    } catch (err) {
      const message = mapUploadError(err, t);
      setFieldError(message);
      toast.error(message);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so re-selecting the same filename after an error fires onChange again.
    e.target.value = '';
    if (!file) return;

    if (!isPdfFile(file)) {
      setFieldError(t('errors.fba_label_not_pdf'));
      return;
    }
    if (file.size > MAX_FBA_LABEL_SIZE) {
      setFieldError(t('errors.fba_label_too_large'));
      return;
    }

    setFieldError(null);
    if (current) {
      pendingFileRef.current = file;
      setConfirmOpen(true);
    } else {
      runUpload(file);
    }
  };

  const handleConfirmClose = (confirmed: boolean) => {
    setConfirmOpen(false);
    const file = pendingFileRef.current;
    pendingFileRef.current = null;
    if (confirmed && file) {
      runUpload(file);
    }
  };

  return (
    <div className="flex flex-col gap-y-8">
      <span className="typo-text-16--bold text-full">{t('outbound.fba_label_title')}</span>
      <div className="flex items-center gap-x-16">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <span className="typo-text-14 text-faded">{current ? current.name : t('outbound.fba_label_empty')}</span>
        <Button
          tone={ButtonToneProps.SECONDARY}
          title={t('outbound.fba_label_upload')}
          disabled={isPending}
          onClick={() => inputRef.current?.click()}
        />
      </div>
      {fieldError && <span className="typo-text-12 status-error-full">{fieldError}</span>}
      <ConfirmationModal
        isOpen={confirmOpen}
        title={t('outbound.fba_label_replace_confirm')}
        description={current?.name ?? ''}
        onClose={handleConfirmClose}
      />
    </div>
  );
};

export default FbaLabel;
